/**
 * Copyright 2019-2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import ErrorComponent from '@/components/AZCustomComponent/ErrorComponent';
import ProgressModal from '@/components/AZCustomComponent/ProgressModal';
import { Grid } from '@/components/Grid';
import { countryCodes } from '@/constants/locale';
import { useAnalytics } from '@/utils/analytics/useAnalytics';
import { usePageViewCall } from '@/utils/analytics/usePageViewCall';
import { getPageName } from '@/utils/analytics/legacyUtils/getPageName';
import { getSeoPageType } from '@/utils/analytics/legacyUtils/getSeoPageType';
import { pageTypes } from '@/utils/analytics/legacyUtils/pageTypes';
import { getAnalyticsRelativeComplementOf } from '@/utils/analytics/getAnalyticsRelativeComplementOf';
import { setupAnalytics } from '@/utils/analytics/config/setupAnalytics';
import { useGetCertonaRecommendations } from '@/features/certona/api/useGetCertonaRecommendations';
import { certonaPageType } from '@/constants/certonaPageType';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useLocale } from '@/hooks/useLocale';
import { PreFooter } from '@/features/prefooter';
import { useRouter } from 'next/router';
import azCommonStyles from '@/theme/globals.module.scss';
import { isEmptyObject } from '@/utils/common';
import { parseUrl, stringifyUrl } from '@/utils/urlHelpers';
import usePrevious from '@/utils/usePrevious';
import React, { useEffect, useRef, useState } from 'react';
import root from 'window-or-global';
import { useCategoryFAQ } from '../../api/getCategoryFAQ';
import styles from './styles.module.scss';
import { useYmmeDefaultData } from '@/features/ymme/utils/useYmmeDefaultData';
import { useInteractionCheck } from '@/hooks/useInteractionCheck';
import { sessionStorage } from '@/utils/sessionStorage';
import { siteSections } from '@/utils/siteSections';
import cx from 'classnames';
import { useSeoMetadata } from '@/features/common/api/getSeoMetadata';
import { useCMSCategoryData } from '../../api/getCMSCategoryData';
import { CMSModularHandler } from '../../components/CMSModularHandler/CMSModularHandler';
import { SeoMetaData } from '../../components/SeoMetaData/SeoMetaData';
import { getShopByPageName } from '../../utils/getShopByPageName';
import { useMappedCategoryData } from '../../api/mapCategoryData';
import { usePageType } from '../../api/getPageType';
import { useSelector } from 'react-redux';
import type { ReduxState } from '@/types';
import { useContentStackLivePreview } from '@/hooks/useContentStackLivePreview';

export const Category = () => {
  const [analyticsDone, setAnalyticsDone] = useState(false);
  const isUtagDefined = useAnalytics();
  const pageViewCall = usePageViewCall();
  const { data: headerData } = useHeaderData();
  const router = useRouter();
  const { data: pageTypeData } = usePageType();
  const { categoryResultsStatus, data: mappedCategoryData } = useMappedCategoryData();
  const categoryRequestFetching = categoryResultsStatus === 'pending';
  const { data: cmsCategoryData, enabled: isCMSCategoryEnabled } = useCMSCategoryData();
  useContentStackLivePreview({ enabled: isCMSCategoryEnabled });
  const { data: ymmeData } = useYmmeDefaultData();
  const defaultEngine = ymmeData.defaultEngine;
  const vehicleId = headerData?.vehicleMap.vehicleId;
  const catalogVehicleId = headerData?.vehicleMap.catalogVehicleId;
  const prevVehicleId = usePrevious(vehicleId);
  const justMounted = useRef(false);
  const scrolled = useRef(false);
  const locale = useLocale();
  const deviceType = useSelector(({ appData }: ReduxState) => appData.deviceType);
  const isABot = deviceType === 'bot';

  const [categoryFAQQueryEnabled, setCategoryFAQQueryEnabled] = useState(false);

  const { data: seoMetadataInfo } = useSeoMetadata({
    vehicleMake: pageTypeData?.make ?? undefined,
    vehicleModel: pageTypeData?.model ?? undefined,
    vehicleYear: pageTypeData?.year ?? undefined,
  });

  const { data: faqs } = useCategoryFAQ({
    enabled: categoryFAQQueryEnabled,
    seoRepositoryType: 'CATEGORY',
    categoryId: pageTypeData?.catalogRepositoryId,
  });

  const categoryId = pageTypeData?.catalogRepositoryId;

  const catLabel = seoMetadataInfo?.pageHeadingName;

  const breadCrumbsList = mappedCategoryData?.breadCrumbsList;
  const seoPageType = pageTypeData?.subPageType;
  const shopByPageType = getShopByPageName(pageTypeData);

  const userInteracted = useInteractionCheck();

  let analyticsPageType = seoPageType;

  if (shopByPageType === 'ShopByModel') {
    if (seoPageType === 'TopNavCategory') {
      analyticsPageType = pageTypes.yearMakeModelNav;
    } else if (seoPageType === 'CategoryGrouping') {
      analyticsPageType = pageTypes.yearMakeModelCategory;
    } else if (seoPageType === 'LeafCategory') {
      analyticsPageType = pageTypes.yearMakeModelSubCategory;
    }
  } else {
    // if none of the above
    if (seoPageType === 'TopNavCategory') {
      analyticsPageType = pageTypes.navigation;
    } else if (seoPageType === 'CategoryGrouping') {
      analyticsPageType = pageTypes.catalog;
    } else if (seoPageType === 'LeafCategory') {
      analyticsPageType = pageTypes.subcategory;
    }
  }

  const getCertonaPageType = () => {
    // Certona on Parent pages:
    if (seoPageType === 'TopNavCategory') {
      return certonaPageType.parentCategoryPage; // Certona on Category and Subcategory pages:
    } else if (pageTypeData?.canonicalPath?.includes('/parts')) {
      // Certona on Parts Subcategory and category pages:
      return seoPageType === 'LeafCategory'
        ? certonaPageType.partsSubCatPage
        : certonaPageType.partsCategoryPage;
    } else {
      // Certona on Accessories Subcategory and category pages:
      return seoPageType === 'LeafCategory'
        ? certonaPageType.accessoriesSubCatPage
        : certonaPageType.accessoriesCategoryPage;
    }
  };

  const certonaRecommendations = useGetCertonaRecommendations(
    {
      certonaType: getCertonaPageType(),
      vehicleID: catalogVehicleId,
      Category1: catLabel,
    },
    [catalogVehicleId, pageTypeData]
  );

  useEffect(() => {
    const acceptableReadyStatus = categoryResultsStatus === 'success';
    if (
      acceptableReadyStatus &&
      isUtagDefined &&
      breadCrumbsList &&
      !isEmptyObject(breadCrumbsList) &&
      !cmsCategoryData &&
      !analyticsDone &&
      userInteracted
    ) {
      if (root.location.href !== root.utag_data?.pageURL) {
        //if coming in from shelf page, useAnalytics gets called before the url changes since shelf and category share the same general route
        //we need to re-setup analytics so it has the correct pageURL in this scenario
        setupAnalytics(false, router.asPath, isUtagDefined);
      }

      let pageType = '';
      let pageName = '';

      if (breadCrumbsList) {
        switch (breadCrumbsList.length) {
          case 2:
            pageType = pageTypes.catalogSection;
            break;

          case 3:
            pageType = pageTypes.catalogCategory;
            break;

          default:
            pageType = pageTypes.catalogSubcategory;
            break;
        }
      }

      breadCrumbsList.map((breadCrumb, index) => {
        if (index !== 0) {
          pageName += breadCrumb.displayName?.toLowerCase().replace(/[^a-z0-9+]+/gi, '-');
        }
      });
      const viewCallObj = {
        pageName: getPageName(pageTypes.catalog, pageName || catLabel || ''),
        siteSection: siteSections.siteSectionCatalog,
        pageType,
        // @ts-expect-error: analyticsPageType can be null or undefined
        seoPageType: getSeoPageType(analyticsPageType, router.asPath),
        seoPopularSearches: !!document.getElementById('#popular-searches-container'),
        seoPopularSearchesClick: sessionStorage.getItem('popularSearchClicked') === 'true',
        pageHierarchy: breadCrumbsList.map((e) => e.displayName).join(' | '),
        contentBlockNames: [],
        contentBlockTypes: [],
      };
      pageViewCall(viewCallObj, getAnalyticsRelativeComplementOf(viewCallObj, undefined));
      setAnalyticsDone(true);
      sessionStorage.setItem('popularSearchClicked', 'false');
      sessionStorage.setItem('performedSearch', 'false');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breadCrumbsList, isUtagDefined, categoryResultsStatus, userInteracted, analyticsDone]);

  useEffect(() => {
    const acceptableReadyStatus = categoryResultsStatus === 'success';
    if (
      acceptableReadyStatus &&
      isUtagDefined &&
      breadCrumbsList &&
      !isEmptyObject(breadCrumbsList) &&
      cmsCategoryData &&
      !analyticsDone &&
      userInteracted
    ) {
      if (root.location.href !== root.utag_data?.pageURL) {
        //if coming in from shelf page, useAnalytics gets called before the url changes since shelf and category share the same general route
        //we need to re-setup analytics so it has the correct pageURL in this scenario
        setupAnalytics(false, router.asPath, isUtagDefined);
      }

      let pageType = '';
      let pageName = '';

      if (breadCrumbsList) {
        switch (breadCrumbsList.length) {
          case 2:
            pageType = pageTypes.catalogSection;
            break;

          case 3:
            pageType = pageTypes.catalogCategory;
            break;

          default:
            pageType = pageTypes.catalogSubcategory;
            break;
        }
      }

      breadCrumbsList.map((breadCrumb, index) => {
        if (index !== 0) {
          pageName += breadCrumb.displayName?.toLowerCase().replace(/[^a-z0-9+]+/gi, '-');
        }
      });
      const viewCallObj = {
        pageName: getPageName(pageTypes.catalog, pageName || catLabel || ''),
        siteSection: siteSections.siteSectionCatalog,
        pageType,
        // @ts-expect-error: analyticsPageType can be null or undefined
        seoPageType: getSeoPageType(analyticsPageType, router.asPath),
        seoPopularSearches: !!document.getElementById('#popular-searches-container'),
        seoPopularSearchesClick: sessionStorage.getItem('popularSearchClicked') === 'true',
        pageHierarchy: breadCrumbsList.map((e) => e.displayName).join(' | '),
      };
      pageViewCall(viewCallObj, getAnalyticsRelativeComplementOf(viewCallObj, undefined));
      setAnalyticsDone(true);
      sessionStorage.setItem('popularSearchClicked', 'false');
      sessionStorage.setItem('performedSearch', 'false');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breadCrumbsList, isUtagDefined, categoryResultsStatus, userInteracted]);

  useEffect(() => {
    // Perform redirect if mappedCategoryData has redirectPage value to match Server side behavior
    if (mappedCategoryData?.redirectPage) {
      void router.replace(mappedCategoryData.redirectPage);
    }
  }, [mappedCategoryData?.redirectPage, router]);

  useEffect(() => {
    const { asPath } = router;
    const { url, query } = parseUrl(asPath);

    if (!justMounted.current) {
      justMounted.current = true;
    } else if (query.vehicleId && vehicleId !== prevVehicleId && !!prevVehicleId) {
      const formattedUrl = stringifyUrl(
        {
          url,
          query: { ...query, vehicleId: null },
        },
        {
          skipNull: true,
        }
      );
      void router.push(formattedUrl);
    } else if (
      //vehicle changed on shop by make pages and vehicle wasn't auto set by ymm data or the auto selected vehicle was changed
      pageTypeData?.makeModelYearPath &&
      vehicleId !== prevVehicleId &&
      (!defaultEngine ||
        (defaultEngine &&
          defaultEngine.value !== `${3}${catalogVehicleId ? catalogVehicleId : ''}`))
    ) {
      const redirectUrl = asPath.replace(pageTypeData?.makeModelYearPath, '');
      void router.push(redirectUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId]);

  useEffect(() => {
    const fetchFaqOnScroll = (): void => {
      if (!scrolled.current && locale === countryCodes.us && !isABot) {
        scrolled.current = true;
        setCategoryFAQQueryEnabled(true);
      } else {
        root.removeEventListener('scroll', fetchFaqOnScroll);
      }
    };

    if (locale === countryCodes.us) {
      root.addEventListener('scroll', fetchFaqOnScroll);
    }

    return () => {
      scrolled.current = false;
      root.removeEventListener('scroll', fetchFaqOnScroll);
    };
  }, [categoryId, locale, isABot]);

  const renderCategory = () => {
    if (cmsCategoryData == null) {
      return null;
    }
    return (
      <Grid container spacing={0}>
        {seoMetadataInfo && (
          // TODO : Implement the rendering of the category Image
          <SeoMetaData categoryImageUrl={''} seoMetaData={seoMetadataInfo} />
        )}

        <Grid item sm={12} md={12} lg={12}>
          {cmsCategoryData.categoryresultheader?.length ? (
            <CMSModularHandler
              catLabel={catLabel ?? ''}
              content={cmsCategoryData.categoryresultheader}
            />
          ) : null}
        </Grid>
        <Grid item sm={12} md={12} lg={12}>
          {cmsCategoryData.categorypagebody?.length ? (
            <CMSModularHandler
              catLabel={catLabel ?? ''}
              content={cmsCategoryData.categorypagebody}
            />
          ) : null}
        </Grid>
        <Grid item sm={12} md={12} lg={12}>
          {cmsCategoryData.categoryresultbottom?.length ? (
            <div className={cx(styles.categoryresultbottom, styles.removeTopMargin)}>
              <CMSModularHandler
                content={cmsCategoryData.categoryresultbottom}
                certonaRecommendations={certonaRecommendations}
                faqs={faqs}
              />
            </div>
          ) : null}
        </Grid>
      </Grid>
    );
  };

  return (
    <div>
      <div
        className={cx(azCommonStyles.mainContentContainer, {
          [styles.placeholderHeight]: categoryRequestFetching,
        })}
      >
        {categoryRequestFetching && <ProgressModal />}
        {!categoryRequestFetching && categoryResultsStatus !== 'error' && renderCategory()}
        {categoryResultsStatus === 'error' && <ErrorComponent />}
      </div>
      {!categoryRequestFetching && <PreFooter />}
    </div>
  );
};
